import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, FileText, Users, Target, Calendar } from "lucide-react";
import { Header } from "@/components/Header";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";

export default function AvaliacaoCaso() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-50 to-green-100 py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold font-primary text-gray-800 mb-6">Avaliação de Caso</h1>
            <p className="text-xl font-secondary text-gray-600 max-w-3xl mx-auto">
              Análise personalizada e gratuita do seu perfil nutricional para definir o melhor plano de tratamento
            </p>
          </div>
        </div>
      </section>

      {/* What is Case Evaluation */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl font-bold font-primary text-gray-800 mb-6">
                O que é a Avaliação de Caso?
              </h2>
              <p className="text-lg font-text text-gray-600 mb-6 leading-relaxed">
                A Avaliação de Caso é um processo inicial onde analisamos seu histórico de saúde, 
                hábitos alimentares, estilo de vida e objetivos para criar uma estratégia 
                nutricional personalizada e eficaz.
              </p>
              <p className="text-lg font-text text-gray-600 mb-6 leading-relaxed">
                Durante essa análise, identificamos as melhores abordagens para seu perfil, 
                considerando suas necessidades específicas, preferências alimentares e rotina diária.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span className="font-text text-gray-700">Análise completa do histórico alimentar</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span className="font-text text-gray-700">Identificação de objetivos e necessidades</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span className="font-text text-gray-700">Proposta de plano de tratamento personalizado</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span className="font-text text-gray-700">Orientações iniciais sem compromisso</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="bg-green-100 rounded-lg p-8 w-full max-w-md">
                <div className="text-center">
                  <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold font-accent mb-2">Avaliação Gratuita</h3>
                  <p className="font-text text-gray-600">
                    Análise inicial sem custo para você conhecer nossa metodologia
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Process Steps */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold font-primary text-gray-800 mb-8 text-center">
              Como Funciona o Processo
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                    1
                  </div>
                  <h3 className="text-lg font-semibold font-accent mb-3">Questionário</h3>
                  <p className="font-text text-gray-600 text-sm">
                    Preencha nosso questionário detalhado sobre sua saúde e hábitos
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                    2
                  </div>
                  <h3 className="text-lg font-semibold font-accent mb-3">Análise</h3>
                  <p className="font-text text-gray-600 text-sm">
                    Nossa equipe analisa suas informações de forma criteriosa
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                    3
                  </div>
                  <h3 className="text-lg font-semibold font-accent mb-3">Proposta</h3>
                  <p className="font-text text-gray-600 text-sm">
                    Recebe uma proposta personalizada de tratamento
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                    4
                  </div>
                  <h3 className="text-lg font-semibold font-accent mb-3">Decisão</h3>
                  <p className="font-text text-gray-600 text-sm">
                    Você decide se quer seguir com o acompanhamento
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Who is it for */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <Card>
              <CardContent className="p-6 text-center">
                <Target className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3 font-accent">Pessoas com Objetivos Específicos</h3>
                <p className="font-text text-gray-600">
                  Emagrecimento, ganho de massa muscular, melhora da saúde ou performance esportiva
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <Users className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3 font-accent">Famílias</h3>
                <p className="font-text text-gray-600">
                  Famílias que querem estabelecer hábitos alimentares saudáveis para todos os membros
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3 font-accent">Pessoas com Condições Específicas</h3>
                <p className="font-text text-gray-600">
                  Diabetes, hipertensão, colesterol alto ou outras condições que precisam de atenção nutricional
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Evaluation Form */}
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold font-primary text-gray-800 mb-4">
                  Solicite sua Avaliação Gratuita
                </h2>
                <p className="text-lg font-secondary text-gray-600">
                  Preencha o formulário abaixo e receba uma análise personalizada do seu caso
                </p>
              </div>

              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-accent text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input 
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-600"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-accent text-gray-700 mb-2">
                      Email *
                    </label>
                    <input 
                      type="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-600"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-accent text-gray-700 mb-2">
                      Telefone/WhatsApp *
                    </label>
                    <input 
                      type="tel"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-600"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-accent text-gray-700 mb-2">
                      Idade
                    </label>
                    <input 
                      type="number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-600"
                      placeholder="Sua idade"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-accent text-gray-700 mb-2">
                    Qual é seu principal objetivo? *
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-600">
                    <option value="">Selecione seu objetivo</option>
                    <option value="emagrecimento">Emagrecimento</option>
                    <option value="ganho-massa">Ganho de massa muscular</option>
                    <option value="saude-geral">Melhoria da saúde geral</option>
                    <option value="performance">Performance esportiva</option>
                    <option value="habitos-familia">Hábitos saudáveis para família</option>
                    <option value="condicao-medica">Condição médica específica</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-accent text-gray-700 mb-2">
                    Conte-nos mais sobre seu caso *
                  </label>
                  <textarea 
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-600"
                    placeholder="Descreva sua situação atual, histórico de tentativas anteriores, condições de saúde relevantes, e qualquer informação que considera importante..."
                  ></textarea>
                </div>

                <div className="flex items-center space-x-3">
                  <input 
                    type="checkbox" 
                    id="terms"
                    className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label htmlFor="terms" className="text-sm font-text text-gray-600">
                    Concordo em receber contato da Dra Brenda Ramos para análise do meu caso
                  </label>
                </div>

                <Button className="w-full bg-green-600 hover:bg-green-700 font-accent">
                  <FileText className="w-5 h-5 mr-2" />
                  Solicitar Avaliação Gratuita
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold font-primary mb-4">
            Tem Dúvidas sobre a Avaliação?
          </h2>
          <p className="text-xl font-secondary mb-8">
            Entre em contato conosco e tire todas suas dúvidas antes de solicitar sua avaliação
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-green-600 hover:bg-green-50 font-accent">
              <Calendar className="w-5 h-5 mr-2" />
              Falar com a Doutora
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-green-600 font-accent">
              Ver Nossos Serviços
            </Button>
          </div>
        </div>
      </section>

      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
